import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Shield,
  Globe,
  Building2,
  Users,
  Leaf,
  FileText,
  ChevronRight,
  Activity,
  Target,
  Clock
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CorporateDashboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [esgData, setEsgData] = useState(null);
  const [supplyChainData, setSupplyChainData] = useState(null);
  const [communityData, setCommunityData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);

  useEffect(() => {
    if (user?.user_type !== 'corporate_esg') {
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [user]);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [dashboard, esg, supplyChain, community, trends] = await Promise.all([
        axios.get(`${API}/corporate/dashboard`, { headers }),
        axios.get(`${API}/corporate/esg-compliance`, { headers }),
        axios.get(`${API}/corporate/supply-chain-risk`, { headers }),
        axios.get(`${API}/corporate/community-impact`, { headers }),
        axios.get(`${API}/corporate/regulatory-trends`, { headers })
      ]);

      setDashboardData(dashboard.data);
      setEsgData(esg.data);
      setSupplyChainData(supplyChain.data);
      setCommunityData(community.data);
      setTrendsData(trends.data);
    } catch (error) {
      console.error('Error fetching corporate data:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6'];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="corporate-loading">
        <Skeleton className="h-10 w-80" />
        <div className="grid md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const riskData = dashboardData ? [
    { name: language === 'es' ? 'Alto Riesgo' : 'High Risk', value: dashboardData.summary.high_risk_count, color: '#ef4444' },
    { name: language === 'es' ? 'Riesgo Medio' : 'Medium Risk', value: dashboardData.summary.medium_risk_count, color: '#f59e0b' },
    { name: language === 'es' ? 'Bajo Riesgo' : 'Low Risk', value: dashboardData.summary.low_risk_count, color: '#22c55e' }
  ] : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="corporate-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-7 w-7 text-primary" />
            {language === 'es' ? 'Portal de Inteligencia Corporativa' : 'Corporate Intelligence Portal'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'es' 
              ? 'Análisis ESG, riesgos de cadena de suministro e impacto comunitario'
              : 'ESG analytics, supply chain risks, and community impact'}
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-sm">
            <Clock className="h-3 w-3 mr-1" />
            {language === 'es' ? 'Última actualización' : 'Last updated'}: {new Date().toLocaleDateString()}
          </Badge>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{language === 'es' ? 'Puntuación ESG' : 'ESG Score'}</p>
                <p className="text-3xl font-bold">{esgData?.overall_esg_score || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Target className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="mt-2 flex items-center text-sm text-success">
              <TrendingUp className="h-4 w-4 mr-1" />
              +3% {language === 'es' ? 'este trimestre' : 'this quarter'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{language === 'es' ? 'Puntuación de Cumplimiento' : 'Compliance Score'}</p>
                <p className="text-3xl font-bold">{dashboardData?.summary.compliance_score || 0}%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-success/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-success" />
              </div>
            </div>
            <Progress value={dashboardData?.summary.compliance_score || 0} className="mt-3 h-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{language === 'es' ? 'Políticas de Alto Riesgo' : 'High Risk Policies'}</p>
                <p className="text-3xl font-bold text-destructive">{dashboardData?.summary.high_risk_count || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {language === 'es' ? 'Requieren atención' : 'Require attention'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{language === 'es' ? 'Impacto Comunitario' : 'Community Impact'}</p>
                <p className="text-3xl font-bold">{communityData?.overall_community_score || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-accent" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {communityData?.community_investments?.total_investment || '$0'} {language === 'es' ? 'invertido' : 'invested'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">{language === 'es' ? 'Resumen' : 'Overview'}</TabsTrigger>
          <TabsTrigger value="esg">{language === 'es' ? 'Cumplimiento ESG' : 'ESG Compliance'}</TabsTrigger>
          <TabsTrigger value="supply-chain">{language === 'es' ? 'Cadena de Suministro' : 'Supply Chain'}</TabsTrigger>
          <TabsTrigger value="community">{language === 'es' ? 'Impacto Comunitario' : 'Community Impact'}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Risk Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  {language === 'es' ? 'Distribución de Riesgos' : 'Risk Distribution'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={riskData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {riskData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  {language === 'es' ? 'Riesgo por Categoría' : 'Risk by Category'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dashboardData?.category_breakdown || []}>
                      <XAxis dataKey="category" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="avg_risk_score" fill="#1a472a" name={language === 'es' ? 'Puntuación de Riesgo' : 'Risk Score'} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent High Risk Policies */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                {language === 'es' ? 'Políticas de Alto Riesgo Recientes' : 'Recent High Risk Policies'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardData?.recent_high_risk_policies?.map((policy, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/policies/${policy.id}`)}
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{policy.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        {policy.jurisdiction} | {policy.category}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="destructive">{policy.risk_score}</Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Regulatory Trends */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                {language === 'es' ? 'Tendencias Regulatorias' : 'Regulatory Trends'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {Object.entries(trendsData?.trend_analysis || {}).map(([key, value]) => (
                  <div key={key} className="p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium capitalize">{key}</h4>
                      <Badge variant={value.direction === 'increasing' ? 'destructive' : 'secondary'}>
                        {value.change_rate}
                      </Badge>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {value.key_developments.slice(0, 2).map((dev, i) => (
                        <li key={i}>• {dev}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ESG Compliance Tab */}
        <TabsContent value="esg" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(esgData?.compliance_metrics || {}).map(([key, value]) => (
              <Card key={key}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="capitalize">{key}</span>
                    <Badge variant={value.trend === 'improving' ? 'default' : 'secondary'}>
                      {value.trend === 'improving' ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                      {value.trend}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold mb-4">{value.score}</div>
                  <div className="space-y-3">
                    {value.metrics.map((metric, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span>{metric.name}</span>
                          <Badge 
                            variant={metric.status === 'compliant' ? 'default' : 'outline'}
                            className={metric.status === 'needs_attention' ? 'border-warning text-warning' : ''}
                          >
                            {metric.score}
                          </Badge>
                        </div>
                        <Progress value={metric.score} className="h-1.5" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Upcoming Deadlines */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {language === 'es' ? 'Próximos Plazos' : 'Upcoming Deadlines'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {esgData?.upcoming_deadlines?.map((deadline, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <h4 className="font-medium text-sm">{deadline.requirement}</h4>
                      <p className="text-xs text-muted-foreground">{deadline.deadline}</p>
                    </div>
                    <Badge 
                      variant={
                        deadline.status === 'on_track' ? 'default' : 
                        deadline.status === 'pending' ? 'outline' : 'secondary'
                      }
                    >
                      {deadline.status.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Supply Chain Tab */}
        <TabsContent value="supply-chain" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {language === 'es' ? 'Evaluación de Riesgo Regional' : 'Regional Risk Assessment'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {supplyChainData?.regional_risk_assessment?.map((region, i) => (
                  <div 
                    key={i} 
                    className={`p-4 rounded-lg border ${
                      region.risk_level === 'high' ? 'border-destructive/50 bg-destructive/5' :
                      region.risk_level === 'medium' ? 'border-warning/50 bg-warning/5' :
                      'border-success/50 bg-success/5'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{region.region}</h4>
                      <Badge 
                        variant={
                          region.risk_level === 'high' ? 'destructive' :
                          region.risk_level === 'medium' ? 'outline' : 'default'
                        }
                      >
                        {region.risk_level}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {region.states.join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                {language === 'es' ? 'Factores de Riesgo Clave' : 'Key Risk Factors'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {supplyChainData?.key_risk_factors?.map((factor, i) => (
                  <div key={i} className="p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{factor.factor}</h4>
                      <div className="flex items-center gap-2">
                        <Badge variant={factor.impact === 'high' ? 'destructive' : 'outline'}>
                          {factor.impact} impact
                        </Badge>
                        <Badge variant="secondary">
                          {factor.trend === 'increasing' ? <TrendingUp className="h-3 w-3 mr-1" /> : null}
                          {factor.trend}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {language === 'es' ? 'Operaciones afectadas' : 'Affected operations'}: {factor.affected_operations.join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {language === 'es' ? 'Recomendaciones' : 'Recommendations'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {supplyChainData?.recommendations?.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
                    {rec}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Community Impact Tab */}
        <TabsContent value="community" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {communityData?.impact_areas?.map((area, i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{area.area}</CardTitle>
                  <CardDescription>{area.affected_population} affected</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-3xl font-bold">{area.impact_score}</span>
                    <Badge variant={area.engagement_status === 'active' ? 'default' : 'secondary'}>
                      {area.engagement_status}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">{language === 'es' ? 'Preocupaciones clave' : 'Key concerns'}:</p>
                    <ul className="text-xs text-muted-foreground">
                      {area.key_concerns.map((concern, j) => (
                        <li key={j}>• {concern}</li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {language === 'es' ? 'Actividades de Participación' : 'Engagement Activities'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {communityData?.engagement_activities?.map((activity, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <h4 className="font-medium text-sm">{activity.activity}</h4>
                        <p className="text-xs text-muted-foreground">{activity.frequency} | Last: {activity.last_date}</p>
                      </div>
                      <Badge variant="outline">{activity.participation} participants</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Leaf className="h-5 w-5" />
                  {language === 'es' ? 'Inversiones Comunitarias' : 'Community Investments'}
                </CardTitle>
                <CardDescription>
                  {language === 'es' ? 'Total' : 'Total'}: {communityData?.community_investments?.total_investment} 
                  <span className="text-success ml-2">{communityData?.community_investments?.year_over_year_change}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {communityData?.community_investments?.breakdown?.map((item, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span>{item.category}</span>
                        <span className="font-medium">{item.amount}</span>
                      </div>
                      <Progress value={item.percentage} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CorporateDashboard;
